<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";

const route = useRoute();
const router = useRouter();
const logger = useLogger("RankedReport");

/** 1) Expect 'id' from the parent */
const props = defineProps<{ id: string }>();

/** 2) Get access to the store's read method only */
const { getContentById } = useContentManager();

/**
 * 3) Read-only computed property wrapping the container definition data.
 */
const definitionData = computed(() => getContentById(props.id));

/** 5) Inject features (editor mode) */
const features = inject("render-features", { editor: false });

/** 6) Commands for context menu (optional) */
const commands = [
  {
    id: "editors-group",
    name: "User Selection",
    description: "Configure this user selection",
    type: "group",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Number of columns",
        action: async () => {
          logger.info("Edit command triggered for user selection", props.id);
          logger.debug("Emitting event for onEdit with user selection data", {
            data: definitionData.value,
          });
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

/** 7) Expose commands & properties for the resource editor / context menu */
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "User selection",
    icon: "lucide:user-round-check",
  },
});

const { reportData, loading, error, fetchReports } = useReportsStore();
const users = ref([]);

onMounted(async () => {
  if (features.editor) {
    logger.info("Editor mode enabled, skipping data fetch");

    users.value = [
      {
        name: "Alice Johnson",
        emailAddress: "alice@example.com",
      },
      {
        name: "Bob Smith",
        emailAddress: "bob@example.com",
      },
      {
        name: "Charlie Brown",
        emailAddress: "charlie@example.com",
      },
      {
        name: "Diana Prince",
        emailAddress: "diana@example.com",
      },
    ];

    return;
  }

  setTimeout(async () => {
    logger.info("Fetching ranked users from API");
    await fetchReports();

    users.value = (
      reportData.value.filter(
        (a) => a.assessment == definitionData.value.assessmentId
      ) || []
    )
      .map((user) => {
        return {
          id: user.userId,
          name: user.name,
          emailAddress: user.emailAddress,
        };
      })
      .sort((a, b) => b.name.localeCompare(a.name));
  }, 1000);
});

function save(newId: string) {
  // If you want to remove the param when it’s empty:
  if (!newId) {
    const { user, ...rest } = route.query;
    return router.replace({ query: { ...rest } });
  }

  // Preserve any other existing query params:
  return router.replace({
    query: {
      ...route.query,
      user: newId,
    },
  });
}
</script>

<template>
  <Card v-if="loading" class="border-0 shadow-none">
    <CardContent class="grid gap-4">
      <div class="text-center">
        <icon mode="svg"
          name="lucide:loader-circle"
          class="mx-auto text-5xl text-primary animate-spin"
        />
        <h3 class="mt-2 text-sm font-semibold text-foreground">
          We're getting things ready
        </h3>
        <p class="mt-1 text-sm text-muted-foreground">
          Please be patient as we're loading some data for you...
        </p>
      </div>
    </CardContent>
  </Card>
  <div v-else>
    <Select :model-value="route.query.user ?? ''" @update:model-value="save">
      <SelectTrigger class="w-[280px]">
        <SelectValue placeholder="Select a user" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>Users</SelectLabel>
          <SelectItem
            v-for="user in users"
            :key="user.id"
            :value="user.id"
          >
            {{ user.name }} ({{ user.emailAddress }})
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  </div>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  editor: () => import("#bb/components/editor/reports/user/index.vue"),
  componentId: ComponentDefinitionIds.USER_SELECT_REPORT,
  icon: "lucide:user-round-check",
  type: "User Selection",
  description: "Select a user for individual reports",
  default: {},
  categories: ["Reports"],
  order: 12004,
};
</script>
